/**
 * Terms Acceptance Intent Tracking (Client-Side Utilities)
 *
 * This module provides client-side utilities for tracking whether a user
 * has agreed to Terms of Service before OAuth redirect. The actual terms
 * acceptance is recorded after successful authentication.
 *
 * Industry Standard Pattern:
 * 1. User checks ToS checkbox on login/signup page
 * 2. Intent is stored in cookie before OAuth redirect
 * 3. After OAuth callback, we read the intent and call acceptTerms mutation
 * 4. Cookie is cleared after processing
 */

const TERMS_INTENT_COOKIE = "terac_terms_accepted";
const TERMS_INTENT_EXPIRY = 60 * 60 * 1000; // 1 hour (same as signup intent)

/**
 * Set terms acceptance intent in cookie (client-side)
 * Call this BEFORE initiating OAuth redirect
 */
export function setTermsAcceptanceIntent(accepted: boolean) {
  if (typeof document === "undefined") {
    return;
  }

  if (accepted) {
    const expires = new Date(Date.now() + TERMS_INTENT_EXPIRY);
    document.cookie = `${TERMS_INTENT_COOKIE}=true; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
  } else {
    // Clear the cookie if not accepted
    clearTermsAcceptanceIntent();
  }
}

/**
 * Get terms acceptance intent from cookie (client-side)
 * Call this AFTER OAuth callback to check if user had accepted terms
 */
export function getTermsAcceptanceIntent(): boolean {
  if (typeof document === "undefined") {
    return false;
  }

  const match = document.cookie.match(
    new RegExp("(^| )" + TERMS_INTENT_COOKIE + "=([^;]+)"),
  );
  const accepted = match ? match[2] === "true" : false;

  return accepted;
}

/**
 * Clear terms acceptance intent cookie (client-side)
 * Call this AFTER processing the intent
 */
export function clearTermsAcceptanceIntent() {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${TERMS_INTENT_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}
